<template>
    <div class="d-flex align-items-center justify-content-center">
        <select
            v-if="type === 'select'"
            v-model="_value"
            class="dbd-editable mr-1"
            :aria-label="ariaLabel"
        >
            <option v-if="placeholder" value="" disabled>{{ placeholder }}</option>
            <option
                v-for="option in ddOptions"
                :key="optionToValue(option)"
                :value="optionToValue(option)"
            >
                {{ option[ddLabelField] || option }}
            </option>
        </select>

        <input
            v-else
            v-select-all-on-focus
            v-model="_value"
            :type="type"
            :aria-label="ariaLabel"
            class="dbd-editable mr-1"
        >

        <template v-if="hasChanged">
            <button class="btn-icon text-success" @click="ok">
                <check-bold-icon/>
            </button>
            <button class="btn-icon text-muted" @click="cancel">
                <close-thick-icon/>
            </button>
        </template>
    </div>
</template>

<script>
import CheckBoldIcon from "nvd-u/icons/CheckBold.vue";
import CloseThickIcon from "nvd-u/icons/CloseThick.vue";

export default {
    name: "Editable",
    components: { CloseThickIcon, CheckBoldIcon },
    props: {
        modelValue: {},
        type: { default: 'text' },
        ddOptions: {
            type: Array,
            default: () => [],
        },
        ddLabelField: {},
        ddValueField: {},
        defaultValue: {},
        ariaLabel: {
            type: String,
            default: ''
        },
        placeholder: {
            type: String,
            default: ''
        },
        needsConfirmation: {
            type: Boolean,
            default: false
        }
    },
    data: () => ({
        _value: '',
    }),
    methods: {
        optionToValue(option) {
            if (option != null && typeof option === 'object' && this.ddValueField != null && option[this.ddValueField] !== undefined) {
                return option[this.ddValueField]
            }
            return option
        },
        /** Match modelValue to an option (case-insensitive) so <select> shows the right row (e.g. ENUM vs enum). */
        resolveSelectModelValue(v) {
            const opts = this.ddOptions || []
            if (v === undefined || v === null || v === '') {
                return this.defaultValue !== undefined && this.defaultValue !== null ? this.defaultValue : ''
            }
            const want = String(v).trim()
            if (!opts.length) return want
            const found = opts.find((opt) => {
                const ov = this.optionToValue(opt)
                return String(ov).trim().toLowerCase() === want.toLowerCase()
            })
            return found !== undefined ? this.optionToValue(found) : want
        },
        syncValueFromModel(v) {
            if (this.type === 'checkbox') {
                this._value = !!v
                return
            }
            if (this.type === 'select') {
                this._value = this.resolveSelectModelValue(v)
                return
            }
            if (v !== undefined && v !== null && v !== '') {
                this._value = v
            } else if (this.defaultValue !== undefined && this.defaultValue !== null) {
                this._value = this.defaultValue
            } else {
                this._value = ''
            }
        },
        ok() {
            this.$emit('update:modelValue', this._value)
        },
        cancel() {
            this.syncValueFromModel(this.modelValue)
            this.$el.querySelector('.dbd-editable').blur()
        },
        updateWidth(w) {
            this.$el.querySelector('.dbd-editable').style.width = `${w + 2}ch`
        },
    },
    computed: {
        hasChanged() {
            const modelValue = this.modelValue ?? ''
            const editableValue = this._value ?? ''
            return modelValue != editableValue
        },
        // needsConfirmation() {
        //     return false
        // return !['select', 'checkbox', 'radio'].includes(this.type)
        // disabled confirmation for all types upon customer request
        // }
    },
    mounted() {
        if (this.type === 'checkbox') {
            this.$emit('update:modelValue', !!this.modelValue)
        }

        this.syncValueFromModel(this.modelValue)
    },
    watch: {
        _value(v) {
            this.updateWidth(v?.length || 3)
            if (!this.needsConfirmation) {
                this.ok()
            }
        },
        modelValue(v) {
            this.syncValueFromModel(v)
        },
        ddOptions: {
            deep: true,
            handler() {
                if (this.type === 'select') {
                    this.syncValueFromModel(this.modelValue)
                }
            },
        },
    },
}
</script>

<style scoped lang="scss">
.dbd-editable {
    padding: 0.25em 0.5em;
    max-width: 20em;
    min-width: 1ch;
    width: 5ch;
    height: fit-content;
    min-height: 2em;
    border: 1px solid #ddd;
    background-color: var(--body-bg);
    flex-grow: 1;
    border-radius: 0.5em;
    color: var(--main-text-color);

    &[type=checkbox], &[type=radio] {
        width: 1em;
        max-width: 1em;
        border: none;
    }

    &[type=color] {
        --color-input-size: 2em;
        padding: 0;
        width: var(--color-input-size);
        height: var(--color-input-size);
        min-height: var(--color-input-size);
        max-width: var(--color-input-size);
    }

    /* Chrome, Safari, Edge, Opera */
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    /* Firefox */
    &[type=number] {
        -moz-appearance: textfield;
    }
}
</style>
